// =============================================================
// LOTO (Lock-Out / Tag-Out) safety gate — pure logic.
//
// Ported from the prototype WO execution (scripts/perpetuum-mobile-app.jsx,
// perpetuum-tablet-app.jsx): technical steps stay LOCKED until the technician
// confirms LOTO; the work order can only complete once LOTO is confirmed, every
// checklist item is done, and the technician has signed.
//
// Binding domain rule (PROJECT.md): "LOTO gate" — the system never silently
// accepts unverified or unsafe work.
// =============================================================

/** State of a work order's execution gate. */
export interface LotoGateInput {
  /** Technician has confirmed lock-out/tag-out of all energy sources. */
  lotoConfirmed: boolean;
  /** Completion flags for each technical checklist item. */
  checklist: boolean[];
  /** Technician has signed off. */
  signed: boolean;
}

/**
 * Are technical steps (checklist, measurements) unlocked?
 * Locked until LOTO is confirmed — mirrors the prototype `gated = !loto`.
 */
export function areStepsUnlocked(lotoConfirmed: boolean): boolean {
  return lotoConfirmed === true;
}

/**
 * Can a checklist item be toggled? Only when LOTO is confirmed.
 * (Prototype: `toggleCheck` no-ops + haptic-warning when `!loto`.)
 */
export function canToggleChecklist(lotoConfirmed: boolean): boolean {
  return areStepsUnlocked(lotoConfirmed);
}

/**
 * May the work order be completed?
 * Requires LOTO confirmed AND every checklist item done AND signed.
 * Mirrors `allDone = loto && checks.every(Boolean) && signed`.
 */
export function canCompleteWorkOrder(input: LotoGateInput): boolean {
  return (
    input.lotoConfirmed === true &&
    input.signed === true &&
    input.checklist.length > 0 &&
    input.checklist.every(Boolean)
  );
}

/** Why completion is blocked — for honest, non-silent UI feedback. */
export type LotoBlockReason =
  | "loto_not_confirmed"
  | "checklist_incomplete"
  | "not_signed"
  | "no_checklist";

/** First blocking reason, or null when the WO may complete. */
export function lotoBlockReason(input: LotoGateInput): LotoBlockReason | null {
  if (!input.lotoConfirmed) return "loto_not_confirmed";
  if (input.checklist.length === 0) return "no_checklist";
  if (!input.checklist.every(Boolean)) return "checklist_incomplete";
  if (!input.signed) return "not_signed";
  return null;
}
